import { useQueries } from "@tanstack/react-query";
import { fetchClient } from "../../API/fetchClient";
import type {
  AnalyticsFilters,
  ModuleAnalyticsResponse,
  AnalyticsDataPoint,
} from "./useGetModuleAnalytics";
import { parseAnalyticsData } from "./useGetModuleAnalytics";

export interface SiteAnalyticsEntry {
  siteId: number;
  siteName: string;
  timeSeries: AnalyticsDataPoint[];
  total: number;
  unit: string;
}

interface UseGetPerSiteAnalyticsOptions {
  baseFilters: Omit<AnalyticsFilters, "site_id">;
  siteIds: number[];
  siteNames: Record<number, string>;
  module: "eau" | "energie";
  enabled?: boolean;
}

/**
 * Fetches analytics for multiple sites in parallel using useQueries.
 * Returns per-site breakdown data for stacked charts and pie charts.
 */
export const useGetPerSiteAnalytics = ({
  baseFilters,
  siteIds,
  siteNames,
  module,
  enabled = true,
}: UseGetPerSiteAnalyticsOptions) => {
  const results = useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: ["perSiteAnalytics", module, siteId, baseFilters],
      queryFn: async (): Promise<SiteAnalyticsEntry> => {
        const filters: Record<string, unknown> = {};
        Object.entries(baseFilters).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            filters[key] = value;
          }
        });
        filters.site_ids = [siteId];

        const endpoint =
          module === "energie" ? `/core/analytics/energie/` : `/core/saisie-analytics/`;

        const response = await fetchClient<ModuleAnalyticsResponse>(endpoint, {
          method: "POST",
          body: filters,
        });

        if (response.error) {
          return {
            siteId,
            siteName: siteNames[siteId] || `Site ${siteId}`,
            timeSeries: [],
            total: 0,
            unit: "",
          };
        }

        const { total, unit, timeSeries } = parseAnalyticsData(response.data);
        return {
          siteId,
          siteName: siteNames[siteId] || `Site ${siteId}`,
          timeSeries,
          total,
          unit,
        };
      },
      enabled: enabled && !!baseFilters.champ_metier && siteIds.length > 0,
      staleTime: 1000 * 60 * 5,
    })),
  });

  const isLoading = results.some((r) => r.isLoading);
  const hasError = results.some((r) => r.isError);
  const siteData: SiteAnalyticsEntry[] = results
    .map((r) => r.data)
    .filter((d): d is SiteAnalyticsEntry => !!d && d.total > 0)
    .sort((a, b) => b.total - a.total);

  // Collect all unique months across all sites
  const allMonths = Array.from(
    new Set(siteData.flatMap((s) => s.timeSeries.map((t) => t.month)))
  ).sort();

  return { siteData, allMonths, isLoading, hasError };
};
